/**
 * <brand-directory> — behaviour for sections/brand-directory.liquid.
 *  - A–Z bar: smooth-scrolls to the letter group (offset via CSS
 *    scroll-margin-top, which accounts for the sticky header), moves focus
 *    there, and marks the letter of the group in view (aria-current).
 *  - Brand search: filters the A–Z tiles by name; empty letter groups and
 *    their letters are disabled while a query is active.
 * Without JS the letters are plain in-page anchors and the search stays hidden.
 */
if (!customElements.get('brand-directory')) {
  class BrandDirectory extends HTMLElement {
    connectedCallback() {
      this.letters = [...this.querySelectorAll('[data-brand-letter]')];
      this.groups = [...this.querySelectorAll('[data-brand-group]')];
      this.searchInput = this.querySelector('[data-brand-search]');
      this.noResults = this.querySelector('[data-brand-no-results]');

      this.handleClick = this.handleClick.bind(this);
      this.addEventListener('click', this.handleClick);

      if (this.searchInput) {
        this.handleSearch = this.handleSearch.bind(this);
        this.searchInput.addEventListener('input', this.handleSearch);
        this.querySelector('[data-brand-search-wrap]').hidden = false;
        // BFCache / autofill can restore a previous query.
        if (this.searchInput.value) this.handleSearch();
      }

      this.initScrollSpy();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      if (this.searchInput) this.searchInput.removeEventListener('input', this.handleSearch);
      if (this.handleScroll) window.removeEventListener('scroll', this.handleScroll);
    }

    handleClick(evt) {
      const link = evt.target.closest('[data-brand-letter]');
      if (!link || link.getAttribute('aria-disabled') === 'true') {
        if (link) evt.preventDefault();
        return;
      }

      const target = document.getElementById(link.hash.slice(1));
      if (!target) return;

      evt.preventDefault();
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
      target.focus({ preventScroll: true });
      window.history.replaceState(null, '', link.hash);
      this.setActiveLetter(link.hash.slice(1));
    }

    handleSearch() {
      const query = this.searchInput.value.trim().toLowerCase();
      let total = 0;

      this.groups.forEach((group) => {
        let matches = 0;
        group.querySelectorAll('[data-brand-name]').forEach((cell) => {
          const match = !query || cell.dataset.brandName.includes(query);
          cell.hidden = !match;
          if (match) matches += 1;
        });
        group.hidden = matches === 0;
        total += matches;

        const letter = this.letters.find((link) => link.hash === `#${group.id}`);
        if (letter) {
          letter.classList.toggle('is-disabled', matches === 0);
          if (matches === 0) {
            letter.setAttribute('aria-disabled', 'true');
          } else {
            letter.removeAttribute('aria-disabled');
          }
        }
      });

      if (this.noResults) this.noResults.hidden = total > 0;
      if (this.handleScroll) this.handleScroll();
    }

    initScrollSpy() {
      if (!this.letters.length) return;

      // Active = the last visible group whose top has passed just under the sticky header.
      let ticking = false;
      this.handleScroll = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          ticking = false;
          const headerHeight = parseFloat(
            getComputedStyle(document.documentElement).getPropertyValue('--header-height')
          ) || 0;
          const line = headerHeight + 48;
          let current = null;
          this.groups.forEach((group) => {
            if (group.hidden) return;
            if (!current || group.getBoundingClientRect().top <= line) current = group;
          });
          if (current) this.setActiveLetter(current.id);
        });
      };
      window.addEventListener('scroll', this.handleScroll, { passive: true });
      this.handleScroll();
    }

    setActiveLetter(groupId) {
      this.letters.forEach((link) => {
        if (link.hash === `#${groupId}`) {
          link.setAttribute('aria-current', 'true');
        } else {
          link.removeAttribute('aria-current');
        }
      });
    }
  }

  customElements.define('brand-directory', BrandDirectory);
}
